import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DisconnectionDetails, useConversation } from '@elevenlabs/react'
import { Orb, OrbStateBadge, type AgentState } from '@/uiV2/elevenLabs/orb'
import Button from '../common/form/inputs/Button'
import { useSafeUserData } from '../../stateStores/User'
import kitApi from '@/api/kit/KitApi'
import ShareInviteCard from '../candidate/components/ShareInviteCard'
import { useShareInvite } from '../candidate/hooks/useShareInvite'
import { Pages } from '../../Pages'

const AGENT_ID = 'agent_1501kg7x9b4needrp9t2nnpmtgze'

const themeVars = {
    '--background': '210 33% 98%',
    '--foreground': '220 20% 20%',
    '--card': '0 0% 100%',
    '--card-foreground': '220 20% 20%',
    '--popover': '0 0% 100%',
    '--popover-foreground': '220 20% 20%',
    '--primary': '217 100% 50%',
    '--primary-foreground': '0 0% 100%',
    '--secondary': '210 25% 96%',
    '--secondary-foreground': '220 20% 30%',
    '--muted': '210 20% 94%',
    '--muted-foreground': '220 10% 50%',
    '--accent': '210 30% 95%',
    '--accent-foreground': '217 100% 45%',
    '--destructive': '0 84% 60%',
    '--destructive-foreground': '0 0% 100%',
    '--border': '210 20% 90%',
    '--input': '210 20% 90%',
    '--ring': '217 100% 50%',
    '--radius': '1.25rem',
    '--shadow-soft': '0 4px 24px -4px hsl(220 20% 20% / 0.08)',
    '--shadow-card': '0 8px 32px -8px hsl(220 20% 20% / 0.1)',
} as React.CSSProperties

const containerClass = 'mx-auto w-full max-w-[1200px] px-6'

export default function HiringManagerKit(): React.JSX.Element {
    const { user } = useSafeUserData()
    const userId = user.id
    const displayName = user.name?.trim() || user.email

    const [error, setError] = useState<string | null>(null)
    const [isStarting, setIsStarting] = useState(false)
    const [isStopping, setIsStopping] = useState(false)
    const [conversationMode, setConversationMode] = useState<
        'speaking' | 'listening' | null
    >(null)
    const [hasConnectedOnce, setHasConnectedOnce] = useState(false)
    const [showThankYou, setShowThankYou] = useState(false)
    const conversationIdRef = useRef<string | null>(null)
    const hasConnectedOnceRef = useRef(false)

    const buildShareInviteMessage = useCallback((shareUrl: string) => {
        return `Kit helped me outline a role I'm hiring for. If you are hiring too, try it here:\n\n${shareUrl}`
    }, [])

    const {
        shareInviteUrl,
        whatsAppShareUrl,
        isMobileShareAvailable,
        isDesktopViewport,
        hasCopiedLink,
        handleCopyInvite,
        handleShareInvite,
    } = useShareInvite({
        landingPath: Pages.HiringManagerKitSignup,
        messageBuilder: buildShareInviteMessage,
        shareTitle: 'Contxt hiring manager kit',
        useEnvShareUrl: false,
    })

    const { startSession, endSession, status, getOutputVolume } =
        useConversation({
            onConnect: ({ conversationId }) => {
                setHasConnectedOnce(true)
                hasConnectedOnceRef.current = true
                setShowThankYou(false)
                setError(null)
                conversationIdRef.current = conversationId || null

                if (conversationId) {
                    void kitApi
                        .startConvoHm({
                            convId: conversationId,
                            personId: user.personId,
                            agentId: AGENT_ID,
                            source: 'hiring_manager_kit',
                        })
                        .catch((kitError) => {
                            console.error(
                                'Failed to log Kit conversation for hiring manager',
                                kitError,
                            )
                        })
                }
            },
            onDisconnect: (details: DisconnectionDetails) => {
                const endedConversationId =
                    conversationIdRef.current?.trim() || null

                if (endedConversationId) {
                    void kitApi
                        .markConvoCallEnded({
                            convId: endedConversationId,
                        })
                        .catch((kitError) => {
                            console.error(
                                'Failed to mark Kit conversation as call ended for hiring manager',
                                {
                                    convId: endedConversationId,
                                    kitError,
                                },
                            )
                        })
                }

                if (details?.reason === 'error') {
                    const message =
                        details?.context instanceof CloseEvent
                            ? details.context.reason ||
                              'Unable to connect to Kit.'
                            : 'Unable to connect to Kit.'
                    if (!hasConnectedOnceRef.current) {
                        setError(message)
                    }
                }

                if (hasConnectedOnceRef.current) {
                    setShowThankYou(true)
                }

                setConversationMode(null)
            },
            onModeChange: ({ mode }) => {
                setConversationMode(mode)
            },
            onError: (message) => {
                setError(message || 'Something went wrong starting the call.')
            },
        })

    useEffect(() => {
        hasConnectedOnceRef.current = hasConnectedOnce
    }, [hasConnectedOnce])

    useEffect(() => {
        return () => {
            endSession().catch((endError) => {
                console.error('Failed to end ElevenLabs session on unmount', {
                    endError,
                })
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleStart = useCallback(async () => {
        if (!userId || status !== 'disconnected') {
            return
        }

        setIsStarting(true)
        setError(null)
        setShowThankYou(false)
        setConversationMode(null)
        conversationIdRef.current = null

        try {
            await navigator.mediaDevices.getUserMedia({ audio: true })
        } catch (permissionError) {
            console.error(
                'Microphone permission was denied for ElevenLabs conversation',
                permissionError,
            )
            setError(
                'We need permission to use your microphone before starting the call.',
            )
            setIsStarting(false)
            return
        }

        try {
            await startSession({
                agentId: AGENT_ID,
                connectionType: 'webrtc',
                userId,
            })
        } catch (startError) {
            console.error('Failed to start ElevenLabs conversation', startError)
            const message =
                startError instanceof Error
                    ? startError.message
                    : 'Unable to start the call right now.'
            setError(message)
        } finally {
            setIsStarting(false)
        }
    }, [startSession, status, userId])

    const handleEnd = useCallback(async () => {
        setIsStopping(true)
        setError(null)

        try {
            await endSession()
        } catch (stopError) {
            console.error('Failed to end ElevenLabs conversation', stopError)
            const message =
                stopError instanceof Error
                    ? stopError.message
                    : 'Unable to end the call. Please try again.'
            setError(message)
        } finally {
            setIsStopping(false)
        }
    }, [endSession])

    const agentState: AgentState = useMemo(() => {
        if (status !== 'connected') return null
        if (conversationMode === 'speaking') return 'talking'
        if (conversationMode === 'listening') return 'listening'
        return null
    }, [conversationMode, status])

    const isConnecting = status === 'connecting' || isStarting
    const isActiveCall =
        status === 'connecting' ||
        status === 'connected' ||
        status === 'disconnecting' ||
        isStarting
    const canStart = status === 'disconnected' && !isStarting
    const showHangUp = status === 'connected' || status === 'disconnecting'
    const canHangUp = status === 'connected' && !isStopping
    const shouldShowShareCard = showThankYou && !isActiveCall

    return (
        <div
            className="min-h-screen bg-background text-foreground font-sans antialiased flex flex-col"
            style={themeVars}
        >
            <header className="w-full py-6">
                <div
                    className={`${containerClass} flex items-center justify-center`}
                >
                    <img
                        src="/Contxt_Logo_Full_BLK_CROPPED.png"
                        alt="Contxt"
                        className="h-10 w-auto"
                    />
                </div>
            </header>

            <main className="flex-1">
                <section className="py-8 md:py-12">
                    <div className={containerClass}>
                        <div className="mx-auto flex w-full max-w-3xl flex-col space-y-8">
                            <div className="rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)]">
                                <h1 className="text-2xl font-semibold text-foreground">
                                    Hi {displayName}
                                </h1>
                                <p className="mt-2 text-sm text-muted-foreground">
                                    Connect with Kit to outline your hiring
                                    needs.
                                </p>
                            </div>

                            {shouldShowShareCard ? (
                                <ShareInviteCard
                                    shareInviteUrl={shareInviteUrl}
                                    whatsAppShareUrl={whatsAppShareUrl}
                                    isMobileShareAvailable={
                                        isMobileShareAvailable
                                    }
                                    isDesktopViewport={isDesktopViewport}
                                    hasCopiedLink={hasCopiedLink}
                                    onCopy={handleCopyInvite}
                                    onShare={handleShareInvite}
                                />
                            ) : null}

                            {!shouldShowShareCard || isActiveCall ? (
                                <div className="rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)]">
                                    <div className="space-y-4">
                                        {error ? (
                                            <p className="text-sm text-destructive">
                                                {error}
                                            </p>
                                        ) : null}

                                        {!isActiveCall ? (
                                            <Button
                                                type="button"
                                                style="primary"
                                                enabled={canStart}
                                                classes="w-full justify-center py-3 text-base shadow-lg shadow-ctxt-blue/20 hover:shadow-xl transition-all"
                                                onClick={handleStart}
                                            >
                                                {isConnecting
                                                    ? 'Connecting…'
                                                    : 'Speak to Kit'}
                                            </Button>
                                        ) : null}

                                        {isActiveCall ? (
                                            <div className="flex w-full flex-col items-center gap-4">
                                                <div className="w-full rounded-2xl border border-border bg-secondary/50 p-4">
                                                    <div className="mx-auto aspect-square max-w-48">
                                                        {status ===
                                                        'connected' ? (
                                                            <Orb
                                                                className="h-full w-full"
                                                                agentState={
                                                                    agentState
                                                                }
                                                                volumeMode={
                                                                    status ===
                                                                    'connected'
                                                                        ? 'manual'
                                                                        : 'auto'
                                                                }
                                                                getOutputVolume={
                                                                    status ===
                                                                    'connected'
                                                                        ? getOutputVolume
                                                                        : undefined
                                                                }
                                                            />
                                                        ) : (
                                                            <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
                                                                Connecting to
                                                                Kit…
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <OrbStateBadge
                                                    agentState={agentState}
                                                />
                                                {showHangUp ? (
                                                    <Button
                                                        type="button"
                                                        style="mild-danger"
                                                        enabled={canHangUp}
                                                        classes="min-w-[140px]"
                                                        onClick={handleEnd}
                                                    >
                                                        {isStopping ||
                                                        status ===
                                                            'disconnecting'
                                                            ? 'Ending…'
                                                            : 'Hang up'}
                                                    </Button>
                                                ) : null}
                                            </div>
                                        ) : null}

                                        {hasConnectedOnce &&
                                        status === 'disconnected' ? (
                                            <p className="text-sm text-muted-foreground">
                                                Call ended. You can reconnect
                                                anytime.
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </section>
            </main>
        </div>
    )
}
